/**
 * Module Registry - Merkezi Modül ve Hizmet Kataloğu
 * 
 * Bu dosya, sistemdeki tüm satılabilir hizmetlerin (modüllerin)
 * teknik tanımlarını, menü yapılarını ve yetki gereksinimlerini içerir.
 */

export const MODULE_CATEGORIES = {
    CORE: 'core',
    POS: 'pos',
    OPERATIONS: 'operations',
    FINANCE: 'finance',
    MARKETING: 'marketing',
    HR: 'hr',
    ECOMMERCE: 'ecommerce',
    INTEGRATION: 'integration'
};

export const SECTORS = {
    ALL: 'all',
    FOOD_BEVERAGE: 'food-beverage',
    RETAIL: 'retail',
    SERVICE: 'service',
    HOSPITALITY: 'hospitality' // 🏨 Hizmet sektörünün otelcilik alt kolu
};

export const MODULE_REGISTRY = {
    // =================================================================
    // ÇEKİRDEK MODÜLLER (CORE)
    // =================================================================
    'core_access': {
        id: 'core_access',
        name: 'Temel İşletme Erişimi',
        description: 'İşletme profili, kullanıcı yönetimi, temel ayarlar ve pano erişimi sağlayan ana modüldür. Tüm işletmeler için zorunludur.',
        category: MODULE_CATEGORIES.CORE,
        price_tier: 'free',
        supported_sectors: [SECTORS.ALL],
        required_permissions: ['basic_access'],
        menu_items: [
            {
                id: 'dashboard',
                label: 'Dashboard',
                path: '/dashboard',
                icon: 'DashboardIcon',
                priority: 1
            },
            {
                id: 'settings',
                label: 'Ayarlar',
                path: '/settings',
                icon: 'SettingsIcon',
                priority: 99
            }
        ]
    },

    // =================================================================
    // POS & SATIŞ MODÜLLERİ
    // =================================================================
    'pos_basic': {
        id: 'pos_basic',
        name: 'Temel POS Sistemi',
        description: 'Hızlı satış, masa açma/kapama, adisyon takibi ve ödeme alma işlemlerini kapsar. Küçük ve orta ölçekli işletmeler için idealdir.',
        category: MODULE_CATEGORIES.POS,
        supported_sectors: [SECTORS.FOOD_BEVERAGE, SECTORS.RETAIL],
        required_permissions: ['pos_access', 'pos_sales'],
        menu_items: [
            {
                id: 'pos_dashboard',
                label: 'POS Dashboard',
                path: '/pos/dashboard',
                icon: 'POSIcon',
                priority: 10
            },
            {
                id: 'quick_sales',
                label: 'Hızlı Satış',
                path: '/pos/quick-sales',
                icon: 'OrderIcon',
                parent: 'pos_group'
            }
        ]
    },

    'table_management': {
        id: 'table_management',
        name: 'Gelişmiş Masa Yönetimi',
        description: 'Restoranlar için kat/salon planı oluşturma, masa birleştirme/taşıma ve doluluk takibi modülü.',
        category: MODULE_CATEGORIES.POS,
        supported_sectors: [SECTORS.FOOD_BEVERAGE],
        required_permissions: ['table_manage'],
        dependencies: ['pos_basic'],
        menu_items: [
            {
                id: 'tables',
                label: 'Masalar',
                path: '/pos/tables',
                icon: 'TableIcon',
                parent: 'pos_group'
            }
        ]
    },

    'kitchen_display': {
        id: 'kitchen_display',
        name: 'Mutfak Ekranı (KDS)',
        description: 'Siparişlerin anlık olarak mutfağa iletilmesini ve hazırlık süreçlerinin (hazırlanıyor, hazır) takibini sağlayan dijital ekran modülü.',
        category: MODULE_CATEGORIES.OPERATIONS,
        supported_sectors: [SECTORS.FOOD_BEVERAGE],
        required_permissions: ['kitchen_access'],
        menu_items: [
            {
                id: 'kitchen_screen',
                label: 'Mutfak',
                path: '/kitchen/screen',
                icon: 'ComputerDesktopIcon',
                priority: 15
            }
        ]
    },

    'qr_menu': {
        id: 'qr_menu',
        name: 'QR Menü & Sipariş',
        description: 'Müşterilerin masadan QR kod okutarak menüyü incelemesini ve garson çağırmadan sipariş vermesini sağlayan dijital menü sistemi.',
        category: MODULE_CATEGORIES.POS,
        supported_sectors: [SECTORS.FOOD_BEVERAGE],
        required_permissions: ['qr_menu_manage'],
        menu_items: [
            {
                id: 'qr_menu_builder',
                label: 'QR Menü',
                path: '/menu/qr-builder',
                icon: 'QrCodeIcon',
                parent: 'marketing_group'
            },
            {
                id: 'qr_analytics',
                label: 'QR Analitik & Trafik',
                path: '/sectors/food-beverage/reports/dashboard',
                icon: 'ChartBarIcon',
                moduleId: 'qr_menu'
            }
        ]
    },

    // =================================================================
    // STOK & ENVANTER
    // =================================================================
    'stock_basic': {
        id: 'stock_basic',
        name: 'Temel Stok & Menü',
        description: 'Ürün reçeteleri oluşturma, menü fiyatlandırma ve basit stok seviyesi takibi. İşletmenin ne sattığını tanımlaması içindir.',
        category: MODULE_CATEGORIES.OPERATIONS,
        supported_sectors: [SECTORS.ALL],
        required_permissions: ['stock_view'],
        menu_items: [
            {
                id: 'products',
                label: 'Ürünler & Menü',
                path: '/products',
                icon: 'TagIcon',
                parent: 'inventory_group'
            }
        ]
    },

    'stock_pro': {
        id: 'stock_pro',
        name: 'Profesyonel Stok Yönetimi',
        description: 'Detaylı envanter sayımı, tedarikçi yönetimi, zayi takibi, depo transferleri ve maliyet analizi gibi ileri seviye stok özelliklerini içerir.',
        category: MODULE_CATEGORIES.OPERATIONS,
        supported_sectors: [SECTORS.RETAIL, SECTORS.FOOD_BEVERAGE],
        required_permissions: ['stock_manage', 'stock_audit'],
        dependencies: ['stock_basic'],
        menu_items: [
            {
                id: 'stock_movements',
                label: 'Stok Hareketleri',
                path: '/inventory/movements',
                icon: 'ArrowsRightLeftIcon',
                parent: 'inventory_group'
            },
            {
                id: 'suppliers',
                label: 'Tedarikçiler',
                path: '/inventory/suppliers',
                icon: 'TruckIcon',
                parent: 'inventory_group'
            }
        ]
    },

    // =================================================================
    // FİNANS & MUHASEBE
    // =================================================================
    'accounting': {
        id: 'accounting',
        name: 'Ön Muhasebe',
        description: 'Gelir-gider takibi, kasa/banka hesapları, fatura yönetimi ve kârlılık raporları sunan kapsamlı finans modülü.',
        category: MODULE_CATEGORIES.FINANCE,
        supported_sectors: [SECTORS.ALL],
        required_permissions: ['accounting_access'],
        menu_items: [
            {
                id: 'finance_dashboard',
                label: 'Finans',
                path: '/finance',
                icon: 'BanknotesIcon',
                priority: 20
            },
            {
                id: 'expenses',
                label: 'Giderler',
                path: '/finance/expenses',
                parent: 'finance_group'
            }
        ]
    },

    // =================================================================
    // CRM & PAZARLAMA
    // =================================================================
    'crm': {
        id: 'crm',
        name: 'Müşteri Kayıtları (CRM)',
        description: 'Müşteri veritabanı oluşturma, iletişim bilgileri saklama ve sipariş geçmişi görüntüleme özelliği.',
        category: MODULE_CATEGORIES.MARKETING,
        supported_sectors: [SECTORS.ALL],
        required_permissions: ['customer_view'],
        menu_items: [
            {
                id: 'customers',
                label: 'Müşteriler',
                path: '/customers',
                icon: 'UserGroupIcon',
                priority: 25
            }
        ]
    },

    'crm_plus': {
        id: 'crm_plus',
        name: 'Gelişmiş CRM & Sadakat',
        description: 'Müşteri segmentasyonu, puan/sadakat sistemi, satış hunisi (pipeline) ve özel müşteri kampanyaları yönetimi.',
        category: MODULE_CATEGORIES.MARKETING,
        supported_sectors: [SECTORS.ALL],
        dependencies: ['crm'],
        menu_items: [
            {
                id: 'crm_pipeline',
                label: 'Satış Fırsatları',
                path: '/crm/pipeline',
                parent: 'crm_group'
            },
            {
                id: 'loyalty',
                label: 'Sadakat Programı',
                path: '/crm/loyalty',
                parent: 'crm_group'
            }
        ]
    },

    'social_media': {
        id: 'social_media',
        name: 'Sosyal Medya Asistanı',
        description: 'Yapay zeka desteği ile sosyal medya içerikleri oluşturma, paylaşım planlama ve hesap yönetimi.',
        category: MODULE_CATEGORIES.MARKETING,
        supported_sectors: [SECTORS.ALL],
        menu_items: [
            {
                id: 'social_media_dashboard',
                label: 'Sosyal Medya',
                path: '/social-media',
                icon: 'ChatBubbleLeftRightIcon',
                priority: 30
            }
        ]
    },

    'whatsapp_integration': {
        id: 'whatsapp_integration',
        name: 'WhatsApp Sipariş & Bildirim',
        description: 'Müşterilere WhatsApp üzerinden otomatik sipariş durumu bildirimleri gönderme ve sipariş alma entegrasyonu.',
        category: MODULE_CATEGORIES.INTEGRATION,
        supported_sectors: [SECTORS.ALL],
        menu_items: [
            {
                id: 'whatsapp',
                label: 'WhatsApp',
                path: '/whatsapp',
                icon: 'ChatBubbleOvalLeftEllipsisIcon',
                priority: 31
            }
        ]
    },

    'sms_notifications': {
        id: 'sms_notifications',
        name: 'SMS Pazarlama',
        description: 'Toplu SMS kampanyaları düzenleme, özel gün kutlamaları ve otomatik bilgilendirme SMS\'leri gönderme.',
        category: MODULE_CATEGORIES.MARKETING,
        supported_sectors: [SECTORS.ALL],
        menu_items: [
            {
                id: 'sms_campaigns',
                label: 'SMS Kampanyaları',
                path: '/marketing/sms',
                parent: 'marketing_group'
            }
        ]
    },

    // =================================================================
    // E-TİCARET
    // =================================================================
    'ecommerce': {
        id: 'ecommerce',
        name: 'E-Ticaret Sitesi',
        description: 'İşletmenize özel online satış sitesi. Kendi alan adınızla sipariş almaya ve online ödeme kabul etmeye başlayın.',
        category: MODULE_CATEGORIES.ECOMMERCE,
        supported_sectors: [SECTORS.RETAIL],
        menu_items: [
            {
                id: 'ecommerce_dashboard',
                label: 'E-Ticaret',
                path: '/ecommerce',
                icon: 'ShoppingCartIcon',
                priority: 40
            },
            {
                id: 'online_orders',
                label: 'Online Siparişler',
                path: '/ecommerce/orders',
                parent: 'ecommerce_group'
            }
        ]
    },

    'marketplace_integrations': {
        id: 'marketplace_integrations',
        name: 'Pazaryeri Entegrasyonları',
        description: 'Trendyol, Hepsiburada, Yemeksepeti, Getir gibi platformlardaki mağazalarınızı tek ekrandan yönetin.',
        category: MODULE_CATEGORIES.INTEGRATION,
        supported_sectors: [SECTORS.RETAIL, SECTORS.FOOD_BEVERAGE],
        menu_items: [
            {
                id: 'marketplaces',
                label: 'Pazaryerleri',
                path: '/integrations/marketplaces',
                icon: 'GlobeAltIcon',
                parent: 'integration_group'
            }
        ]
    },

    // =================================================================
    // İK & YÖNETİM
    // =================================================================
    'hr_management': {
        id: 'hr_management',
        name: 'Personel & Vardiya (İK)',
        description: 'Çalışan kayıtları, vardiya planlama, izin takibi ve performans değerlendirme işlemleri.',
        category: MODULE_CATEGORIES.HR,
        supported_sectors: [SECTORS.ALL],
        menu_items: [
            {
                id: 'staff',
                label: 'Personel',
                path: '/hr/staff',
                icon: 'UsersIcon',
                priority: 50
            },
            {
                id: 'shifts',
                label: 'Vardiya Planı',
                path: '/hr/shifts',
                parent: 'hr_group'
            }
        ]
    },

    'multi_branch': {
        id: 'multi_branch',
        name: 'Çoklu Şube & Franchise Yönetimi',
        description: 'Birden fazla şubesi veya franchise işletmesi olan markalar için merkezi yönetim, şube politikaları, akıllı sipariş yönlendirme ve konsolide raporlama modülü.',
        category: MODULE_CATEGORIES.CORE,
        price_tier: 'enterprise',
        supported_sectors: [SECTORS.ALL],
        required_permissions: ['franchise_manage', 'multi_branch_access'],
        menu_items: [
            {
                id: 'brands_management',
                label: 'İşletmelerim & Şubeler',
                path: '/brands',
                icon: 'BuildingOfficeIcon',
                priority: 85
            },
            {
                id: 'franchise_settings',
                label: 'Franchise Politikaları',
                path: '/business/settings-dashboard',
                icon: 'Cog6ToothIcon',
                priority: 86
            },
            {
                id: 'franchise_users',
                label: 'Şube Kullanıcıları',
                path: '/business/user-management-dashboard',
                icon: 'UsersIcon',
                priority: 87
            },
            {
                id: 'franchise_analytics',
                label: 'Konsolide Analitikler',
                path: '/business/analytics-dashboard',
                icon: 'ChartBarIcon',
                priority: 88
            }
        ]
    },

    // =================================================================
    // RAPORLAMA
    // =================================================================
    'reporting_basic': {
        id: 'reporting_basic',
        name: 'Temel Raporlar',
        description: 'Gün sonu raporları, satış özetleri ve ciro takibi gibi işletmenin temel performans göstergeleri.',
        category: MODULE_CATEGORIES.OPERATIONS,
        supported_sectors: [SECTORS.ALL],
        menu_items: [
            {
                id: 'reports',
                label: 'Raporlar',
                path: '/reports',
                icon: 'ChartBarIcon',
                priority: 60
            }
        ]
    },

    'reporting_advanced': {
        id: 'reporting_advanced',
        name: 'İş Zekası & Analitik',
        description: 'Detaylı ürün bazlı kârlılık, saatlik yoğunluk analizleri, müşteri alışkanlıkları ve tahminleme raporları.',
        category: MODULE_CATEGORIES.OPERATIONS,
        supported_sectors: [SECTORS.ALL],
        dependencies: ['reporting_basic'],
        menu_items: [
            {
                id: 'analytics',
                label: 'Analitik',
                path: '/reports/analytics',
                parent: 'reports_group'
            }
        ]
    },

    'api_access': {
        id: 'api_access',
        name: 'Geliştirici API Erişimi',
        description: 'Özel yazılımlarınızın veya 3. parti uygulamaların sistemle konuşabilmesi için API anahtarı ve dokümantasyon erişimi.',
        category: MODULE_CATEGORIES.INTEGRATION,
        supported_sectors: [SECTORS.ALL],
        price_tier: 'pro',
        menu_items: [
            {
                id: 'api_settings',
                label: 'API Ayarları',
                path: '/settings/api',
                icon: 'CommandLineIcon',
                parent: 'settings_group'
            }
        ]
    },

    // =================================================================
    // 🏨 OTELCİLİK & TURİZM MODÜLLERİ (HOSPITALITY)
    // =================================================================
    'room_management': {
        id: 'room_management',
        name: 'Oda & Tip Yönetimi',
        description: 'Oda tipleri, fiyatlandırma, fotoğraf galerisi, olanaklar ve ek opsiyonların tanımlanması. QR Menü yönetimindeki mantığın otelcilik karşılığı.',
        category: MODULE_CATEGORIES.OPERATIONS,
        supported_sectors: [SECTORS.HOSPITALITY],
        required_permissions: ['room_view', 'room_manage'],
        menu_items: [
            {
                id: 'room_management',
                label: 'Oda Yönetimi',
                path: '/hospitality/rooms',
                icon: 'BuildingOffice2Icon',
                priority: 10
            }
        ]
    },

    'booking_engine': {
        id: 'booking_engine',
        name: 'Online Rezervasyon Motoru',
        description: 'QR Menü gibi müşteri tarafı rezervasyon web sitesi: otel anasayfası, oda listeleme, tarih seçimi, misafir formu ve WhatsApp/ödeme ile tamamlama. SEO uyumlu.',
        category: MODULE_CATEGORIES.ECOMMERCE,
        supported_sectors: [SECTORS.HOSPITALITY],
        required_permissions: ['booking_manage'],
        menu_items: [
            {
                id: 'hotel_website',
                label: 'Rezervasyon Sitesi',
                path: '/hospitality/settings/website',
                icon: 'GlobeAltIcon',
                priority: 11
            }
        ]
    },

    'reservation_management': {
        id: 'reservation_management',
        name: 'Rezervasyon Yönetimi',
        description: 'Takvim ve liste görünümü ile rezervasyon takibi, check-in/check-out işlemleri, misafir kayıtları ve manuel rezervasyon oluşturma.',
        category: MODULE_CATEGORIES.OPERATIONS,
        supported_sectors: [SECTORS.HOSPITALITY],
        required_permissions: ['reservation_view', 'reservation_manage'],
        dependencies: ['room_management'],
        menu_items: [
            {
                id: 'reservations',
                label: 'Rezervasyonlar',
                path: '/hospitality/reservations',
                icon: 'CalendarDaysIcon',
                priority: 12
            },
            {
                id: 'reservation_calendar',
                label: 'Doluluk Takvimi',
                path: '/hospitality/reservations/calendar',
                icon: 'CalendarIcon',
                parent: 'reservations_group'
            }
        ]
    },

    'hotel_finance': {
        id: 'hotel_finance',
        name: 'Otel Gelir & Doluluk Analizi',
        description: 'Otelcilik özelinde ADR, RevPAR, doluluk oranı metrikleri ile birlikte gelir/gider takibi. Mevcut muhasebe modülü üzerine otel katmanı.',
        category: MODULE_CATEGORIES.FINANCE,
        supported_sectors: [SECTORS.HOSPITALITY],
        required_permissions: ['accounting_access'],
        dependencies: ['accounting'],
        menu_items: [
            {
                id: 'hotel_finance_dashboard',
                label: 'Finans & Doluluk',
                path: '/hospitality/finance',
                icon: 'BanknotesIcon',
                priority: 30
            },
            {
                id: 'occupancy_report',
                label: 'Doluluk Raporu',
                path: '/hospitality/finance/occupancy',
                icon: 'ChartBarIcon',
                parent: 'hotel_finance_group'
            },
            {
                id: 'ads_roi',
                label: 'Reklam ROI',
                path: '/hospitality/finance/ads-roi',
                icon: 'MegaphoneIcon',
                parent: 'hotel_finance_group'
            }
        ]
    },

    'hotel_whatsapp': {
        id: 'hotel_whatsapp',
        name: 'WhatsApp Otel Rezervasyon Botu',
        description: 'Yapay zeka destekli WhatsApp botu: Müşterinin tarih/kişi sayısı bilgilerine göre müsait odaları listeler, fiyat bildirir ve rezervasyon linki gönderir. Personel olmadan 7/24 rezervasyon.',
        category: MODULE_CATEGORIES.INTEGRATION,
        supported_sectors: [SECTORS.HOSPITALITY],
        dependencies: ['reservation_management'],
        menu_items: [
            {
                id: 'hotel_whatsapp_bot',
                label: 'WhatsApp Rezervasyon Botu',
                path: '/business/whatsapp',
                icon: 'ChatBubbleOvalLeftEllipsisIcon',
                priority: 35
            }
        ]
    },

    'channel_manager': {
        id: 'channel_manager',
        name: 'Kanal Yöneticisi',
        description: 'Booking.com, Enuygun.com, Hotels.com gibi online seyahat acentelerindeki odaları tek panelden yönetin. İleri aşama entegrasyon.',
        category: MODULE_CATEGORIES.INTEGRATION,
        supported_sectors: [SECTORS.HOSPITALITY],
        price_tier: 'pro',
        dependencies: ['reservation_management'],
        menu_items: [
            {
                id: 'channel_manager',
                label: 'Kanal Yöneticisi',
                path: '/hospitality/channels',
                icon: 'PuzzlePieceIcon',
                parent: 'integration_group',
                badge: 'Yakında'
            }
        ]
    }
};

/**
 * Modül Grubu Tanımları (Sidebar'daki gruplar)
 */
export const MENU_GROUPS = {
    'pos_group': {
        id: 'pos_group',
        label: 'POS & Satış',
        icon: 'POSIcon',
        priority: 10
    },
    'inventory_group': {
        id: 'inventory_group',
        label: 'Stok & Envanter',
        icon: 'CubeIcon',
        priority: 20
    },
    'finance_group': {
        id: 'finance_group',
        label: 'Finansal Yönetim',
        icon: 'BanknotesIcon',
        priority: 30
    },
    'marketing_group': {
        id: 'marketing_group',
        label: 'Pazarlama',
        icon: 'MegaphoneIcon',
        priority: 40
    },
    'crm_group': {
        id: 'crm_group',
        label: 'Müşteri İlişkileri',
        icon: 'UserGroupIcon',
        priority: 45
    },
    'ecommerce_group': {
        id: 'ecommerce_group',
        label: 'E-Ticaret',
        icon: 'ShoppingCartIcon',
        priority: 50
    },
    'hr_group': {
        id: 'hr_group',
        label: 'İnsan Kaynakları',
        icon: 'UsersIcon',
        priority: 60
    },
    'reports_group': {
        id: 'reports_group',
        label: 'Raporlar ve Analiz',
        icon: 'ChartBarIcon',
        priority: 70
    },
    'integration_group': {
        id: 'integration_group',
        label: 'Entegrasyonlar',
        icon: 'PuzzlePieceIcon',
        priority: 80
    },
    'settings_group': {
        id: 'settings_group',
        label: 'Ayarlar',
        icon: 'SettingsIcon',
        priority: 100
    },
    // 🏨 Otelcilik Grupları
    'reservations_group': {
        id: 'reservations_group',
        label: 'Rezervasyonlar',
        icon: 'CalendarDaysIcon',
        priority: 12
    },
    'hotel_finance_group': {
        id: 'hotel_finance_group',
        label: 'Finans & Analizler',
        icon: 'BanknotesIcon',
        priority: 30
    },
    'hotel_settings_group': {
        id: 'hotel_settings_group',
        label: 'Otel Ayarları',
        icon: 'Cog6ToothIcon',
        priority: 90
    }
};

export default MODULE_REGISTRY;
